<template>
  <div class="alice-shell theme-turquoise">
    <TopBar class="alice-shell__topbar">
      <template #breadcrumb>
        <span class="alice-tb-crumb alice-tb-crumb--current">Previews</span>
      </template>
    </TopBar>

    <Sidebar
      class="alice-shell__sidebar"
      variant="rail"
      active-key="previews"
    />

    <main class="alice-shell__main">
      <PageHeader
        title="Design previews"
        :count="previews.length"
        subtitle="Put a static HTML/CSS/JS build live on a shareable link — iterate, tweak with AI, roll back. Cleaned up after 6 months of inactivity."
      />

      <div class="pv-toolbar alice-shell__toolbar">
        <div class="pv-toolbar__search">
          <Icon name="search" :size="16" />
          <input v-model="query" type="text" placeholder="Search previews…" />
        </div>
        <button class="pv-toolbar__new" @click="openNew">
          <Icon name="plus" :size="14" /><span>New preview</span>
        </button>
      </div>

      <div class="alice-shell__list pv-list">
        <div v-if="loading" class="pv-empty">
          <v-progress-circular indeterminate size="28" width="3" />
        </div>
        <div v-else-if="!filtered.length" class="pv-empty">
          {{
            previews.length
              ? "No previews match your search."
              : "No previews yet — drop a designer build on a live link in under a minute."
          }}
        </div>

        <div v-else class="pv-grid">
          <article v-for="p in filtered" :key="p.slug" class="pv-card">
            <!-- Screenshot of the live version (thumbnail.js) -->
            <div class="pv-shot">
              <img
                v-if="thumbUrl(p)"
                :src="thumbUrl(p)"
                alt=""
                loading="lazy"
                class="pv-shot__img"
                @error="brokenThumbs[p.thumb.url] = true"
              />
              <div
                v-else
                class="pv-shot__empty"
                :class="`pv-shot__empty--${shotPlaceholder(p).kind}`"
              >
                <VP1Pattern
                  class="pv-shot__pattern"
                  :size="72"
                  :opacity="0.07"
                />
                <span class="pv-shot__label">{{
                  shotPlaceholder(p).label
                }}</span>
              </div>

              <a
                v-if="isServing(p)"
                class="pv-shot__link"
                :href="urlOf(p)"
                target="_blank"
                rel="noopener"
                :aria-label="`Open ${p.name}`"
              >
                <span class="pv-shot__open"
                  >Open <Icon name="external" :size="12"
                /></span>
              </a>

              <button
                v-if="shotPlaceholder(p).action === 'upload'"
                class="pv-shot__cta"
                :disabled="isBusy(p)"
                @click="openUpload(p)"
              >
                {{ p.status === "draft" ? "Upload the site" : "Upload again" }}
              </button>
              <button
                v-else-if="
                  !thumbUrl(p) && shotPlaceholder(p).action === 'shoot'
                "
                class="pv-shot__cta"
                @click="refreshThumb(p)"
              >
                {{ p.thumb && p.thumb.error ? "Try again" : "Take screenshot" }}
              </button>

              <span v-if="thumbUrl(p) && thumbPending(p)" class="pv-shot__badge"
                >Updating screenshot…</span
              >
              <div v-if="isBusy(p)" class="pv-shot__busy">
                <v-progress-circular indeterminate size="18" width="2" />
                Publishing…
              </div>
            </div>

            <div class="pv-card__body">
              <div class="pv-card__chips">
                <span
                  class="pv-state"
                  :class="`pv-state--${stateOf(p).kind}`"
                  >{{ stateOf(p).label }}</span
                >
                <span
                  v-if="isProtected(p)"
                  class="pv-state pv-state--locked"
                  :class="{ 'pv-state--locked-out': allCodesExpired(p) }"
                  :title="
                    allCodesExpired(p)
                      ? 'Password protected — all codes expired; visitors are locked out'
                      : 'Password protected — visitors need an access code'
                  "
                  ><Icon name="lock" :size="10" /> protected</span
                >
                <span
                  v-if="expiryWarning(p)"
                  class="pv-state pv-state--expiring"
                  :title="`Expires ${shortDate(
                    p.expiresAt
                  )} — renew or publish to keep it live`"
                  >{{ expiryWarning(p) }}</span
                >
              </div>

              <h3 class="pv-card__title" :title="p.name">{{ p.name }}</h3>

              <!-- Check report: one line here, details in a dialog -->
              <button
                v-if="showReport(p)"
                class="pv-checks"
                :class="`pv-checks--${p.checkReport.verdict}`"
                @click="reportFor = p.slug"
              >
                <span
                  class="pv-report__dot"
                  :class="`pv-report__dot--${p.checkReport.verdict}`"
                ></span>
                <span>{{ reportLabel(p) }}</span>
                <Icon name="chevron-right" :size="12" />
              </button>

              <!-- Rollback picker -->
              <div v-if="versionsFor === p.slug" class="pv-item__versions">
                <span class="pv-item__versions-label">Live version:</span>
                <button
                  v-for="v in versionOptions(p)"
                  :key="v.n"
                  class="pv-version"
                  :class="{ 'pv-version--live': v.n === p.liveVersion }"
                  :disabled="v.n === p.liveVersion || isBusy(p)"
                  :title="`${v.source === 'tweak' ? 'AI tweak' : 'Upload'} by ${
                    v.byName
                  } — ${shortDate(v.createdAt)}`"
                  @click="rollback(p, v.n)"
                >
                  v{{ v.n }}
                </button>
              </div>

              <footer class="pv-card__foot">
                <div class="pv-card__who">
                  <div class="pv-card__owner">
                    {{ p.ownerName || p.ownerEmail }}
                  </div>
                  <div
                    class="pv-card__meta"
                    :title="`Expires ${shortDate(p.expiresAt)}`"
                  >
                    <template v-if="p.liveVersion >= 1"
                      >v{{ p.liveVersion }} ·
                    </template>
                    updated {{ shortDate(p.updatedAt) }}
                  </div>
                </div>
                <div class="pv-item__actions">
                  <a
                    v-if="isServing(p)"
                    class="pv-iconbtn"
                    :href="urlOf(p)"
                    target="_blank"
                    rel="noopener"
                    title="Open live preview"
                  >
                    <Icon name="external" :size="16" />
                  </a>
                  <button
                    v-if="isServing(p)"
                    class="pv-iconbtn"
                    :title="copied === p.slug ? 'Copied!' : 'Copy link'"
                    @click="copyLink(p)"
                  >
                    <Icon name="copy" :size="16" />
                  </button>
                  <div class="pv-menu-wrap">
                    <button
                      class="pv-iconbtn"
                      :class="{ 'is-active': menuFor === p.slug }"
                      title="More actions"
                      aria-haspopup="menu"
                      :aria-expanded="menuFor === p.slug ? 'true' : 'false'"
                      @click.stop="menuFor = menuFor === p.slug ? null : p.slug"
                    >
                      <Icon name="more" :size="16" />
                    </button>
                    <div
                      v-if="menuFor === p.slug"
                      class="pv-menu"
                      role="menu"
                      @click.stop
                    >
                      <button
                        class="pv-menu__item"
                        role="menuitem"
                        :disabled="isBusy(p)"
                        @click="
                          menuFor = null;
                          openUpload(p);
                        "
                      >
                        {{
                          p.status === "draft"
                            ? "Upload the site"
                            : "Upload a new version"
                        }}
                      </button>
                      <button
                        v-if="p.liveVersion >= 1 && p.status !== 'expired'"
                        class="pv-menu__item"
                        role="menuitem"
                        :disabled="isBusy(p)"
                        @click="
                          menuFor = null;
                          openTweak(p);
                        "
                      >
                        Ask AI to tweak
                      </button>
                      <button
                        v-if="versionOptions(p).length"
                        class="pv-menu__item"
                        role="menuitem"
                        :disabled="isBusy(p)"
                        @click="
                          menuFor = null;
                          versionsFor = versionsFor === p.slug ? null : p.slug;
                        "
                      >
                        Switch live version…
                      </button>
                      <button
                        v-if="isServing(p)"
                        class="pv-menu__item"
                        role="menuitem"
                        :disabled="thumbPending(p)"
                        @click="
                          menuFor = null;
                          refreshThumb(p);
                        "
                      >
                        Refresh screenshot
                      </button>
                      <button
                        class="pv-menu__item"
                        role="menuitem"
                        @click="
                          menuFor = null;
                          openPasswords(p);
                        "
                      >
                        {{
                          isProtected(p)
                            ? "Manage access codes"
                            : "Protect with access codes"
                        }}
                      </button>
                      <button
                        v-if="p.status !== 'expired'"
                        class="pv-menu__item"
                        role="menuitem"
                        @click="
                          menuFor = null;
                          renew(p);
                        "
                      >
                        Renew for 6 months
                      </button>
                      <button
                        class="pv-menu__item pv-menu__item--danger"
                        role="menuitem"
                        :disabled="isBusy(p)"
                        @click="
                          menuFor = null;
                          remove(p);
                        "
                      >
                        Delete preview
                      </button>
                    </div>
                  </div>
                </div>
              </footer>
            </div>
          </article>
        </div>
      </div>
    </main>

    <!-- Click-away catcher for the row "⋯" menu -->
    <div v-if="menuFor" class="pv-menu-backdrop" @click="menuFor = null" />

    <!-- New preview dialog -->
    <v-dialog v-model="dialog" max-width="460">
      <v-card class="alice-new-dialog">
        <div class="alice-new-dialog__title">New preview</div>
        <input
          v-model="fName"
          type="text"
          autofocus
          class="pv-pw__input pv-pw__input--block"
          placeholder="Name (e.g. Pitch deck micro-site)"
          @keydown.enter="create"
        />
        <div class="alice-new-dialog__actions">
          <button
            type="button"
            class="pv-pw__btn pv-pw__btn--ghost"
            @click="dialog = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="pv-pw__btn pv-pw__btn--primary"
            :disabled="!fName.trim() || creating"
            @click="create"
          >
            {{ creating ? "Creating…" : "Create" }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Access codes dialog -->
    <v-dialog v-model="pwDialog" max-width="560">
      <v-card v-if="pwPreview" class="alice-new-dialog">
        <div class="alice-new-dialog__title">Access codes</div>
        <div class="pv-pw__toggle">
          <button
            type="button"
            class="pv-switch"
            :class="{ 'is-on': pwProtected }"
            role="switch"
            :aria-checked="pwProtected ? 'true' : 'false'"
            :disabled="pwSaving"
            @click="setProtected(!pwProtected)"
          >
            <span class="pv-switch__knob"></span>
          </button>
          <div class="pv-pw__toggle-text">
            <div class="pv-pw__toggle-title">
              {{ pwProtected ? "Password protected" : "Public" }}
            </div>
            <div class="pv-pw__toggle-sub">
              {{
                pwProtected
                  ? "Visitors must enter one of the access codes below."
                  : "Anyone with the link can open this preview."
              }}
            </div>
          </div>
        </div>

        <template v-if="pwProtected">
          <ul v-if="pwEntries.length" class="pv-pw__list">
            <li
              v-for="c in pwEntries"
              :key="c.id"
              class="pv-pw__row"
              :class="{ 'is-expired': c.expiresAt <= now }"
            >
              <code class="pv-pw__value">{{ c.value }}</code>
              <button
                class="pv-iconbtn"
                :title="copiedPw === c.id ? 'Copied!' : 'Copy code'"
                @click="copyCode(c)"
              >
                <Icon name="copy" :size="14" />
              </button>
              <span class="pv-pw__meta">
                <template v-if="c.label">{{ c.label }} · </template>
                {{
                  c.expiresAt <= now
                    ? "expired"
                    : `expires ${shortDate(c.expiresAt)}`
                }}
              </span>
              <button
                class="pv-iconbtn pv-iconbtn--danger"
                :disabled="pwEntries.length <= 1"
                :title="
                  pwEntries.length <= 1
                    ? 'A protected preview needs at least one code — turn off protection to remove it'
                    : 'Delete code (visitors using it lose access within a minute)'
                "
                @click="removeCode(c)"
              >
                <Icon name="trash" :size="14" />
              </button>
            </li>
          </ul>

          <div class="pv-pw__add">
            <div class="pv-pw__add-label">Add a code</div>
            <div class="pv-pw__add-row">
              <input
                v-model="fPwValue"
                type="text"
                class="pv-pw__input"
                placeholder="Access code (min 6 characters)"
                @keydown.enter="addCode"
              />
              <button type="button" class="pv-pw__btn" @click="generateCode">
                Generate
              </button>
            </div>
            <div class="pv-pw__add-row">
              <input
                v-model="fPwLabel"
                type="text"
                class="pv-pw__input"
                placeholder="Label (e.g. Client X) — optional"
              />
              <input
                v-model="fPwDate"
                type="date"
                class="pv-pw__input pv-pw__date-input"
                title="Expiry date"
              />
            </div>
            <div v-if="pwError" class="pv-pw__error">{{ pwError }}</div>
          </div>
        </template>
        <div v-else class="pv-pw__public-note">
          This preview is public — anyone with the link can open it. Turn
          protection on to require an access code (your codes are kept).
          <div v-if="pwError" class="pv-pw__error">{{ pwError }}</div>
        </div>

        <div class="alice-new-dialog__actions">
          <button
            type="button"
            class="pv-pw__btn pv-pw__btn--ghost"
            @click="pwFor = null"
          >
            Close
          </button>
          <button
            v-if="pwProtected"
            type="button"
            class="pv-pw__btn pv-pw__btn--primary"
            :disabled="fPwValue.trim().length < 6 || !fPwDate || pwSaving"
            @click="addCode"
          >
            {{ pwSaving ? "Adding…" : "Add code" }}
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Check report dialog -->
    <v-dialog v-model="reportDialog" max-width="560">
      <v-card v-if="reportPreview" class="alice-new-dialog">
        <div class="alice-new-dialog__title">Checks</div>
        <div class="pv-pw__toggle-sub pv-report__for">
          {{ reportPreview.name }}
        </div>
        <div
          class="pv-report"
          :class="`pv-report--${reportPreview.checkReport.verdict}`"
        >
          <div class="pv-report__summary">
            {{ reportPreview.checkReport.summary }}
          </div>
          <ul
            v-if="(reportPreview.checkReport.issues || []).length"
            class="pv-report__issues"
          >
            <li
              v-for="(issue, ii) in reportPreview.checkReport.issues"
              :key="ii"
            >
              <strong>{{ issue.severity }}</strong>
              <template v-if="issue.file"> · {{ issue.file }}</template>
              — {{ issue.message }}
            </li>
          </ul>
        </div>
        <div class="alice-new-dialog__actions">
          <button
            type="button"
            class="pv-pw__btn pv-pw__btn--ghost"
            @click="reportFor = null"
          >
            Close
          </button>
          <button
            v-if="reportPreview.checkReport.verdict === 'block'"
            type="button"
            class="pv-pw__btn pv-pw__btn--primary"
            :disabled="isBusy(reportPreview)"
            @click="
              openUpload(reportPreview);
              reportFor = null;
            "
          >
            Upload again
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Upload drawer -->
    <div v-if="uploadFor" class="pv-drawer">
      <div class="pv-drawer__panel">
        <header class="pv-drawer__head">
          <div>
            <h2 class="pv-drawer__title">
              {{
                uploadFor.liveVersion >= 1
                  ? "Upload new version"
                  : "Upload site"
              }}
            </h2>
            <div class="pv-drawer__subtitle">{{ uploadFor.name }}</div>
          </div>
          <button
            class="pv-drawer__close"
            title="Close"
            :disabled="uploadBusy"
            @click="uploadFor = null"
          >
            <Icon name="chevron-right" :size="18" />
          </button>
        </header>
        <div class="pv-drawer__body">
          <PreviewUploadZone
            :slug="uploadFor.slug"
            @busy="uploadBusy = $event"
            @published="onPublished"
            @blocked="onBlocked"
          />
        </div>
      </div>
    </div>

    <!-- AI tweak drawer -->
    <div v-if="tweakFor" class="pv-drawer">
      <div class="pv-drawer__panel">
        <PreviewTweakChat
          :preview="tweakFor"
          @close="tweakFor = null"
          @published="onTweaked"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { collection, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/firebase";

import TopBar from "@/components/alice/TopBar.vue";
import Sidebar from "@/components/alice/Sidebar.vue";
import PageHeader from "@/components/alice/PageHeader.vue";
import Icon from "@/components/alice/Icon.vue";
import VP1Pattern from "@/components/alice/VP1Pattern.vue";
import PreviewUploadZone from "@/components/alice/PreviewUploadZone.vue";
import PreviewTweakChat from "@/components/alice/PreviewTweakChat.vue";

// Mirrors PREVIEW_BASE_URL in functions/modules/previews/store.js.
const PREVIEW_BASE_URL = "https://valsplat-previews.web.app";
const DAY_MS = 24 * 60 * 60 * 1000;
const THUMB_PENDING_MS = 3 * 60 * 1000;

export default {
  name: "PreviewsPage",
  components: {
    TopBar,
    Sidebar,
    PageHeader,
    Icon,
    VP1Pattern,
    PreviewUploadZone,
    PreviewTweakChat,
  },
  data() {
    return {
      previews: [],
      loading: true,
      unsubscribe: null,
      query: "",
      dialog: false,
      fName: "",
      creating: false,
      uploadFor: null,
      uploadBusy: false,
      tweakFor: null,
      versionsFor: null,
      menuFor: null, // slug of the preview whose "⋯" menu is open
      reportFor: null, // slug of the preview whose check report is open
      brokenThumbs: {}, // thumb url → true once the image failed to load
      clock: null,
      copied: null,
      // Access codes dialog
      pwFor: null, // slug of the preview whose codes are open
      fPwValue: "",
      fPwLabel: "",
      fPwDate: "",
      pwSaving: false,
      pwError: "",
      copiedPw: null,
      now: Date.now(), // ticks every 15s: code expiry + screenshot timeouts
    };
  },
  computed: {
    pwDialog: {
      get() {
        return !!this.pwFor;
      },
      set(v) {
        if (!v) this.pwFor = null;
      },
    },
    reportDialog: {
      get() {
        return !!this.reportPreview;
      },
      set(v) {
        if (!v) this.reportFor = null;
      },
    },
    reportPreview() {
      const p = this.previews.find((x) => x.slug === this.reportFor);
      return p && p.checkReport ? p : null;
    },
    pwPreview() {
      // Live doc from the snapshot, so add/delete reflect instantly.
      return this.previews.find((p) => p.slug === this.pwFor) || null;
    },
    pwEntries() {
      return [...((this.pwPreview && this.pwPreview.passwords) || [])].sort(
        (a, b) => (a.createdAt || 0) - (b.createdAt || 0)
      );
    },
    pwProtected() {
      return !!this.pwPreview && this.pwPreview.isPublic !== true;
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      const sorted = [...this.previews].sort(
        (a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)
      );
      if (!q) return sorted;
      return sorted.filter((p) =>
        [p.name, p.slug, p.ownerName, p.ownerEmail, p.status]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  mounted() {
    this.unsubscribe = onSnapshot(
      collection(db, "previews"),
      (snapshot) => {
        this.previews = snapshot.docs.map((d) => ({ ...d.data(), id: d.id }));
        this.loading = false;
        // Keep open drawers pointed at fresh doc data.
        if (this.uploadFor) {
          this.uploadFor =
            this.previews.find((p) => p.slug === this.uploadFor.slug) || null;
        }
        if (this.tweakFor) {
          this.tweakFor =
            this.previews.find((p) => p.slug === this.tweakFor.slug) ||
            this.tweakFor;
        }
      },
      (err) => {
        console.error("previews snapshot:", err);
        this.loading = false;
      }
    );
    this.clock = setInterval(() => (this.now = Date.now()), 15 * 1000);
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
    clearInterval(this.clock);
  },
  methods: {
    urlOf(p) {
      return `${PREVIEW_BASE_URL}/${p.slug}/`;
    },
    isBusy(p) {
      // A fresh lock means a publish/tweak is in flight (stale locks are
      // stolen server-side after 15 min).
      return !!(p.lock && p.lock.at && Date.now() - p.lock.at < 15 * 60 * 1000);
    },
    isServing(p) {
      return (
        p.liveVersion >= 1 && p.status !== "expired" && p.status !== "draft"
      );
    },
    stateOf(p) {
      if (this.isBusy(p)) return { kind: "busy", label: "Publishing…" };
      const map = {
        draft: { kind: "draft", label: "Draft" },
        live: { kind: "live", label: "Live" },
        needs_attention: { kind: "attention", label: "Needs attention" },
        expired: { kind: "expired", label: "Expired" },
      };
      return map[p.status] || { kind: "draft", label: p.status || "—" };
    },
    expiryWarning(p) {
      if (p.status === "expired" || !p.expiresAt) return "";
      const days = Math.ceil((p.expiresAt - Date.now()) / DAY_MS);
      if (days > 30) return "";
      return days <= 1 ? "expires today" : `expires in ${days}d`;
    },
    showReport(p) {
      const r = p.checkReport;
      if (!r) return false;
      if (r.verdict === "block" || r.verdict === "warn") return true;
      // A passing report can still carry info notes worth showing — e.g. which
      // build-output folder or landing page we picked.
      return (r.issues || []).some((i) => i.severity === "info");
    },
    // ------------- screenshots -------------
    // capturePreviewThumbnail (a doc trigger) writes `thumb` for the live
    // version; it is behind while the live version moved on or a refresh was
    // asked for after the last capture.
    thumbPending(p) {
      if (!this.isServing(p)) return false;
      const t = p.thumb || {};
      const behind =
        t.version !== p.liveVersion || (p.thumbRequestedAt || 0) > (t.at || 0);
      // A capture takes ~15s; past the window, stop promising one (e.g. it
      // crashed, or the preview predates screenshots).
      const since = Math.max(p.updatedAt || 0, p.thumbRequestedAt || 0);
      return behind && this.now - since < THUMB_PENDING_MS;
    },
    thumbUrl(p) {
      // Shown while a newer capture is pending — a slightly stale screenshot
      // beats an empty card.
      const url = p.thumb && p.thumb.url;
      if (!url || p.status === "expired" || this.brokenThumbs[url]) return null;
      return url;
    },
    shotPlaceholder(p) {
      if (p.status === "expired") return { kind: "expired", label: "Expired" };
      if (!(p.liveVersion >= 1)) {
        return p.status === "needs_attention"
          ? { kind: "blocked", label: "Upload blocked", action: "upload" }
          : { kind: "draft", label: "Nothing uploaded yet", action: "upload" };
      }
      if (this.thumbPending(p)) {
        return { kind: "pending", label: "Taking screenshot…" };
      }
      if (p.thumb && p.thumb.error) {
        return { kind: "missing", label: "Screenshot failed", action: "shoot" };
      }
      return { kind: "missing", label: "No screenshot yet", action: "shoot" };
    },
    async refreshThumb(p) {
      try {
        const call = httpsCallable(functions, "refreshPreviewThumbnail");
        await call({ slug: p.slug });
        this.now = Date.now();
      } catch (e) {
        alert((e && e.message) || "Could not refresh the screenshot.");
      }
    },
    reportLabel(p) {
      const issues = (p.checkReport && p.checkReport.issues) || [];
      const n = (sev) => issues.filter((i) => i.severity === sev).length;
      const parts = [];
      const blockers = n("blocker");
      const warnings = n("warning");
      const infos = n("info");
      if (blockers) parts.push(`${blockers} blocker${blockers > 1 ? "s" : ""}`);
      if (warnings) parts.push(`${warnings} warning${warnings > 1 ? "s" : ""}`);
      if (infos) parts.push(`${infos} info`);
      return parts.length ? `Checks: ${parts.join(" · ")}` : "Check report";
    },
    versionOptions(p) {
      return Object.entries(p.versions || {})
        .map(([n, v]) => ({ n: Number(n), ...v }))
        .sort((a, b) => b.n - a.n);
    },
    shortDate(ts) {
      if (!ts) return "—";
      return new Date(ts).toLocaleDateString("nl-NL", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    },
    // ------------- actions -------------
    openNew() {
      this.fName = "";
      this.dialog = true;
    },
    async create() {
      const name = this.fName.trim();
      if (!name || this.creating) return;
      this.creating = true;
      try {
        const call = httpsCallable(functions, "createPreview");
        const res = await call({ name });
        this.dialog = false;
        // Open the upload drawer for the new preview straight away; the
        // snapshot will swap in the real doc as soon as it arrives.
        this.uploadFor = {
          slug: res.data.slug,
          name,
          liveVersion: 0,
          status: "draft",
        };
      } catch (e) {
        console.error("createPreview:", e);
        alert((e && e.message) || "Could not create the preview.");
      } finally {
        this.creating = false;
      }
    },
    openUpload(p) {
      this.tweakFor = null;
      this.uploadFor = p;
    },
    openTweak(p) {
      this.uploadFor = null;
      this.tweakFor = { ...p, url: this.urlOf(p) };
    },
    onPublished() {
      this.uploadFor = null;
    },
    onBlocked() {
      // Close the drawer and show the doc's checkReport details.
      this.reportFor = this.uploadFor && this.uploadFor.slug;
      this.uploadFor = null;
    },
    onTweaked() {
      // Keep the chat open — the designer may want to iterate further.
    },
    async copyLink(p) {
      try {
        await navigator.clipboard.writeText(this.urlOf(p));
        this.copied = p.slug;
        setTimeout(() => {
          if (this.copied === p.slug) this.copied = null;
        }, 1500);
      } catch (e) {
        console.error("copy failed:", e);
      }
    },
    // ------------- access codes -------------
    isProtected(p) {
      return p.isPublic !== true;
    },
    hasValidCode(p) {
      const now = Date.now();
      return (p.passwords || []).some((c) => c.expiresAt > now);
    },
    allCodesExpired(p) {
      // "Locked out": protected but nothing valid gets a visitor in.
      return this.isProtected(p) && !this.hasValidCode(p);
    },
    async openPasswords(p) {
      this.now = Date.now();
      this.pwFor = p.slug;
      this.fPwValue = "";
      this.fPwLabel = "";
      this.pwError = "";
      // Default expiry: 30 days out.
      const d = new Date(Date.now() + 30 * DAY_MS);
      this.fPwDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(d.getDate()).padStart(2, "0")}`;
      // A protected preview must always have a code. Legacy previews (protected
      // by default, never touched) may have none yet — materialize one so the
      // owner has something to share and the preview is reachable.
      if (this.isProtected(p) && !this.hasValidCode(p)) {
        this.setProtected(true);
      }
    },
    async setProtected(on) {
      if (this.pwSaving) return;
      this.pwSaving = true;
      this.pwError = "";
      try {
        const call = httpsCallable(functions, "setPreviewPublic");
        await call({ slug: this.pwFor, isPublic: !on });
        this.now = Date.now();
      } catch (e) {
        this.pwError = (e && e.message) || "Could not change protection.";
      } finally {
        this.pwSaving = false;
      }
    },
    generateCode() {
      // Readable, unambiguous (no 0/O/1/l/I), ~47 bits.
      const alphabet = "abcdefghjkmnpqrstuvwxyz23456789";
      const bytes = new Uint8Array(10);
      crypto.getRandomValues(bytes);
      let code = "";
      for (let i = 0; i < bytes.length; i++) {
        if (i === 5) code += "-";
        code += alphabet[bytes[i] % alphabet.length];
      }
      this.fPwValue = code;
    },
    async addCode() {
      const value = this.fPwValue.trim();
      if (value.length < 6 || !this.fPwDate || this.pwSaving) return;
      // End of the chosen day, local time.
      const expiresAt = new Date(`${this.fPwDate}T23:59:59`).getTime();
      this.pwSaving = true;
      this.pwError = "";
      try {
        const call = httpsCallable(functions, "addPreviewPassword");
        await call({
          slug: this.pwFor,
          value,
          label: this.fPwLabel.trim(),
          expiresAt,
        });
        this.fPwValue = "";
        this.fPwLabel = "";
        this.now = Date.now();
      } catch (e) {
        this.pwError = (e && e.message) || "Could not add the code.";
      } finally {
        this.pwSaving = false;
      }
    },
    async removeCode(c) {
      const ok = confirm(
        `Delete this access code${
          c.label ? ` (${c.label})` : ""
        }? Visitors using it lose access within a minute.`
      );
      if (!ok) return;
      try {
        const call = httpsCallable(functions, "deletePreviewPassword");
        await call({ slug: this.pwFor, id: c.id });
      } catch (e) {
        this.pwError = (e && e.message) || "Could not delete the code.";
      }
    },
    async copyCode(c) {
      try {
        await navigator.clipboard.writeText(c.value);
        this.copiedPw = c.id;
        setTimeout(() => {
          if (this.copiedPw === c.id) this.copiedPw = null;
        }, 1500);
      } catch (e) {
        console.error("copy failed:", e);
      }
    },
    async rollback(p, version) {
      try {
        const call = httpsCallable(functions, "rollbackPreview");
        await call({ slug: p.slug, version });
        this.versionsFor = null;
      } catch (e) {
        alert((e && e.message) || "Rollback failed.");
      }
    },
    async renew(p) {
      try {
        const call = httpsCallable(functions, "renewPreview");
        await call({ slug: p.slug });
      } catch (e) {
        alert((e && e.message) || "Renew failed.");
      }
    },
    async remove(p) {
      const ok = confirm(
        `Delete "${p.name}"? The live link stops working immediately and the files are gone for good.`
      );
      if (!ok) return;
      try {
        const call = httpsCallable(functions, "deletePreview");
        await call({ slug: p.slug });
        if (this.uploadFor?.slug === p.slug) this.uploadFor = null;
        if (this.tweakFor?.slug === p.slug) this.tweakFor = null;
      } catch (e) {
        alert((e && e.message) || "Delete failed.");
      }
    },
  },
};
</script>

<style scoped lang="scss">
/* ===== shell (same grid as Changelog/Requests) ===== */
.alice-shell {
  display: grid;
  grid-template-rows: 56px 1fr;
  grid-template-columns: 56px 1fr;
  height: 100vh;
  width: 100%;
  background: var(--bg-muted);
  font-family: var(--font-body);
  color: var(--fg-1);
  overflow: hidden;
}
.alice-shell__topbar {
  grid-column: 1 / -1;
  grid-row: 1;
}
.alice-shell__sidebar {
  grid-column: 1;
  grid-row: 2;
}
/* The header scrolls away with the list; only the toolbar stays in view. */
.alice-shell__main {
  grid-column: 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow-y: auto;
}
.alice-shell__main > * {
  flex-shrink: 0;
}
.alice-shell__toolbar {
  position: sticky;
  top: 0;
  z-index: 3;
}
.alice-shell__list {
  flex-grow: 1;
  background: var(--vp-white);
}

/* ===== toolbar (same idiom as the Changelog toolbar) ===== */
.pv-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 32px;
  background: var(--vp-white);
  border-bottom: 1px solid var(--border-hairline);
}
.pv-toolbar__search {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  color: var(--fg-3);
}
.pv-toolbar__search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--fg-1);
  background: transparent;
}
.pv-toolbar__new {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 18px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.pv-toolbar__new:hover {
  background: var(--accent-100);
  color: var(--vp-black);
}

/* ===== list ===== */
.pv-list {
  padding: 24px 32px 40px;
}
.pv-empty {
  padding: 60px 0;
  text-align: center;
  color: var(--fg-3);
  font-family: var(--font-ui);
  font-size: 14px;
}
.pv-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(340px, 100%), 1fr));
  gap: 20px;
}
.pv-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
}

/* ----- screenshot ----- */
.pv-shot {
  position: relative;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: var(--bg-muted);
  border-bottom: 1px solid var(--border-hairline);
}
.pv-shot__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top center;
}
.pv-shot__link {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 10px;
  outline: 2px solid transparent;
  outline-offset: -2px;
  transition: outline-color var(--dur-fast) var(--ease-hover);
}
.pv-shot__link:hover,
.pv-shot__link:focus-visible {
  outline-color: var(--accent-100);
}
.pv-shot__open {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  background: var(--vp-black);
  color: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0;
  transform: translateY(4px);
  transition: opacity var(--dur-fast) var(--ease-out),
    transform var(--dur-fast) var(--ease-out);
}
.pv-shot__link:hover .pv-shot__open,
.pv-shot__link:focus-visible .pv-shot__open {
  opacity: 1;
  transform: none;
}
.pv-shot__empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.pv-shot__pattern {
  position: absolute;
  inset: 0;
}
.pv-shot__label {
  position: relative;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--fg-3);
}
/* Room for the CTA under the label. */
.pv-shot__empty--draft .pv-shot__label,
.pv-shot__empty--blocked .pv-shot__label,
.pv-shot__empty--missing .pv-shot__label {
  margin-bottom: 52px;
}
.pv-shot__empty--blocked {
  background: var(--vp-rood-10);
}
.pv-shot__empty--blocked .pv-shot__label {
  color: var(--vp-rood-140);
}
.pv-shot__empty--expired .pv-shot__label {
  text-decoration: line-through;
}
.pv-shot__empty--pending::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    rgba(255, 255, 255, 0.7) 50%,
    transparent 70%
  );
  background-size: 250% 100%;
  animation: pv-shimmer 1.6s linear infinite;
}
@keyframes pv-shimmer {
  from {
    background-position: 100% 0;
  }
  to {
    background-position: -150% 0;
  }
}
@media (prefers-reduced-motion: reduce) {
  .pv-shot__empty--pending::after {
    animation: none;
  }
  .pv-shot__open {
    transition: none;
  }
}
.pv-shot__cta {
  position: absolute;
  left: 50%;
  top: calc(50% + 14px);
  transform: translateX(-50%);
  z-index: 1;
  height: 36px;
  padding: 0 16px;
  border: 1px solid var(--vp-black);
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-hover);
}
.pv-shot__cta:hover:not(:disabled) {
  background: var(--accent-20);
}
.pv-shot__cta:disabled {
  opacity: 0.5;
  cursor: default;
}
.pv-shot__badge {
  position: absolute;
  left: 10px;
  top: 10px;
  padding: 3px 8px;
  background: var(--vp-white);
  border: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-2);
  pointer-events: none;
}
.pv-shot__busy {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.82);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-2);
}

/* ----- body ----- */
.pv-card__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px 16px 12px;
  min-width: 0;
}
.pv-card__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pv-card__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--vp-black);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.pv-card__foot {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--border-hairline);
}
.pv-card__who {
  flex: 1;
  min-width: 0;
}
.pv-card__owner {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  color: var(--vp-black);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pv-card__meta {
  font-family: var(--font-ui);
  font-size: 11px;
  color: var(--fg-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.pv-item__actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.pv-iconbtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--fg-3);
  cursor: pointer;
}
.pv-iconbtn:hover:not(:disabled) {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.pv-iconbtn:disabled {
  opacity: 0.35;
  cursor: default;
}
.pv-iconbtn--danger:hover:not(:disabled) {
  color: var(--vp-rood-100, #c62828);
}
.pv-iconbtn.is-active {
  color: var(--vp-black);
  background: var(--bg-muted);
}

/* ===== "⋯" actions menu (same idiom as recordings) ===== */
.pv-menu-wrap {
  position: relative;
  display: inline-flex;
}
.pv-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 40;
}
.pv-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  min-width: 196px;
  display: flex;
  flex-direction: column;
  background: var(--vp-white);
  border: 1px solid var(--vp-black);
  box-shadow: 4px 4px 0 var(--border-hairline);
}
.pv-menu__item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 600;
  color: var(--vp-black);
  white-space: nowrap;
}
.pv-menu__item:hover:not(:disabled) {
  background: var(--bg-muted);
}
.pv-menu__item:disabled {
  opacity: 0.5;
  cursor: default;
}
.pv-menu__item--danger {
  color: var(--vp-rood-100, #c62828);
  border-top: 1px solid var(--border-hairline);
}
.pv-menu__item--danger:hover:not(:disabled) {
  background: var(--vp-rood-10, #fdecec);
}

/* ===== state chips ===== */
.pv-state {
  flex-shrink: 0;
  padding: 2px 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.pv-state--live {
  background: var(--accent-20);
  color: var(--vp-black);
}
.pv-state--draft {
  background: var(--bg-muted);
  color: var(--fg-3);
}
.pv-state--busy {
  background: var(--bg-muted);
  color: var(--fg-2);
}
.pv-state--attention {
  background: #fdecec;
  color: #c62828;
}
.pv-state--expired {
  background: var(--bg-muted);
  color: var(--fg-3);
  text-decoration: line-through;
}
.pv-state--expiring {
  background: #fff4e0;
  color: #a86400;
}
.pv-state--locked {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--bg-muted);
  color: var(--fg-2);
}
.pv-state--locked-out {
  background: #fff4e0;
  color: #a86400;
}

/* ===== access codes dialog ===== */
.pv-pw__intro {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
  margin-bottom: 14px;
}
.pv-pw__empty {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-3);
  padding: 12px 0;
}
.pv-pw__toggle {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0 16px;
  margin-bottom: 14px;
  border-bottom: 1px solid var(--border-hairline);
}
.pv-pw__toggle-title {
  font-family: var(--font-ui);
  font-size: 14px;
  font-weight: 700;
  color: var(--fg-1);
}
.pv-pw__toggle-sub {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-switch {
  flex-shrink: 0;
  width: 40px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: var(--border-hairline);
  cursor: pointer;
  transition: background 0.15s ease;
}
.pv-switch.is-on {
  background: var(--vp-black);
}
.pv-switch:disabled {
  opacity: 0.5;
  cursor: default;
}
.pv-switch__knob {
  display: block;
  width: 18px;
  height: 18px;
  margin: 3px;
  border-radius: 50%;
  background: var(--vp-white);
  transition: transform 0.15s ease;
}
.pv-switch.is-on .pv-switch__knob {
  transform: translateX(16px);
}
.pv-pw__public-note {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--fg-2);
  padding: 4px 0 8px;
}
.pv-iconbtn:disabled {
  opacity: 0.35;
  cursor: default;
}
.pv-pw__list {
  list-style: none;
  margin: 0 0 14px;
  padding: 0;
}
.pv-pw__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-hairline);
}
.pv-pw__row.is-expired .pv-pw__value {
  text-decoration: line-through;
  color: var(--fg-3);
}
.pv-pw__value {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px;
  font-weight: 600;
  color: var(--fg-1);
}
.pv-pw__meta {
  flex: 1;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  text-align: right;
}
.pv-pw__add {
  margin-top: 4px;
  padding-top: 14px;
  border-top: 1px solid var(--border-hairline);
}
.pv-pw__add-label {
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--fg-3);
  margin-bottom: 10px;
}
.pv-pw__add-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.pv-pw__error {
  font-family: var(--font-ui);
  font-size: 13px;
  color: var(--vp-rood-100);
  margin-top: 4px;
}

/* Native inputs/buttons — the sharp, hairline style used across the app,
   replacing Vuetify's Material look inside these dialogs. */
.pv-pw__input {
  flex: 1;
  min-width: 0;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 14px;
  color: var(--fg-1);
  outline: none;
}
.pv-pw__input:focus {
  border-color: var(--vp-black);
}
.pv-pw__input::placeholder {
  color: var(--fg-3);
}
.pv-pw__input--block {
  width: 100%;
  flex: none;
  margin-bottom: 14px;
}
.pv-pw__add-row .pv-pw__input.pv-pw__date-input {
  flex: 0 0 170px;
}
.pv-pw__btn {
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  color: var(--vp-black);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
}
.pv-pw__btn:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.pv-pw__btn--primary {
  background: var(--vp-black);
  color: var(--vp-white);
  border-color: var(--vp-black);
}
.pv-pw__btn--primary:disabled {
  opacity: 0.4;
  cursor: default;
}
.pv-pw__btn--ghost {
  border-color: transparent;
  background: transparent;
  color: var(--fg-2);
}
.pv-pw__btn--ghost:hover {
  color: var(--vp-black);
}

/* ===== versions ===== */
.pv-item__versions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.pv-item__versions-label {
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-version {
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--border-hairline);
  background: var(--vp-white);
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  color: var(--vp-black);
}
.pv-version:hover:not(:disabled) {
  border-color: var(--vp-black);
}
.pv-version--live {
  background: var(--vp-black);
  color: var(--vp-white);
  cursor: default;
}
.pv-version:disabled:not(.pv-version--live) {
  opacity: 0.4;
  cursor: default;
}

/* ===== check report ===== */
.pv-checks {
  display: flex;
  align-items: center;
  gap: 6px;
  align-self: stretch;
  padding: 6px 8px;
  margin: 0 -8px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  text-align: left;
}
.pv-checks span:nth-child(2) {
  flex: 1;
}
.pv-checks:hover {
  color: var(--vp-black);
  background: var(--bg-muted);
}
.pv-checks--warn {
  color: #6b4a00;
  background: #fff4e0;
}
.pv-checks--block {
  color: #7a1f1f;
  background: #fdecec;
}
.pv-report__for {
  margin: -8px 0 14px;
}
.pv-report__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.pv-report__dot--warn {
  background: #a86400;
}
.pv-report__dot--block {
  background: #c62828;
}
.pv-report__dot--pass {
  background: var(--fg-3);
}
.pv-report {
  margin-bottom: 16px;
  padding: 10px 14px;
  font-family: var(--font-ui);
  font-size: 13px;
}
.pv-report--warn {
  background: #fff4e0;
  color: #6b4a00;
}
.pv-report--block {
  background: #fdecec;
  color: #7a1f1f;
}
.pv-report--pass {
  background: var(--bg-muted);
  color: var(--fg-2);
}
.pv-report__issues {
  margin: 8px 0 0;
  padding-left: 18px;
}
.pv-report__issues li {
  margin-top: 4px;
}

/* ===== drawers ===== */
.pv-drawer {
  position: fixed;
  top: 56px;
  right: 0;
  bottom: 0;
  width: 440px;
  max-width: 90vw;
  z-index: 40;
  box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);
}
.pv-drawer__panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--vp-white);
  border-left: 1px solid var(--border-hairline);
}
.pv-drawer__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
  border-bottom: 1px solid var(--border-hairline);
}
.pv-drawer__title {
  margin: 0;
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--vp-black);
}
.pv-drawer__subtitle {
  margin-top: 2px;
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
}
.pv-drawer__close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--fg-3);
  padding: 4px;
}
.pv-drawer__close:hover:not(:disabled) {
  color: var(--vp-black);
}
.pv-drawer__body {
  padding: 20px;
  overflow-y: auto;
}

/* ===== new dialog (shared idiom with Changelog) ===== */
.alice-new-dialog {
  padding: 20px;
}
.alice-new-dialog__title {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 14px;
}
.alice-new-dialog__input {
  margin-bottom: 12px;
}
.alice-new-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
